import { useState, useEffect, useCallback, useRef } from 'react';
import { api, AdminRole, Folder, FolderWithMeta, FileUpload, UploadSearchScope, OrgProject } from '../api';
import { formatFileSize, getFileType, FileIcon, FolderIcon, getErrorMessage, usePaginatedList, useDebouncedValue, highlightMatch, busyTracker } from '../../../shared/src';
import { Sentry } from '../../../shared/src/sentry';
import { triggerBrowserDownload } from '../utils/downloadFile';
import {
  downloadFolderAsZip,
  downloadFolderInParts,
  FileFetchError,
  FolderEmptyError,
  FolderPartLocation,
  FolderPartsError,
  FolderTooLargeError,
  DOWNLOAD_PAGE_SIZE,
  FolderDownloadProgress,
  FolderPartsProgress,
} from '../utils/downloadFolderZip';
import { FolderDownloadModal, FolderDownloadView } from './FolderDownloadModal';
import { MoveFolderModal } from './MoveFolderModal';
import './UploadsTab.css';

interface UploadsTabProps {
  orgId: string;
  /** The project being browsed; required for "search entire project". */
  projectId?: string;
  currentFolderId: string | null;
  onFolderChange: (folderId: string | null) => void;
  /** @deprecated use projectRootFolderId instead */
  rootFolderId?: string | null;
  projectRootFolderId?: string | null;
  projectName?: string;
  onBackToProjects?: () => void;
  adminRole: AdminRole;
  /** Other projects in the org — destinations for the "move folder" action. */
  availableProjects?: OrgProject[];
}

function FolderPathIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg className="search-input-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <circle cx="11" cy="11" r="7" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

type FolderDownloadState =
  | {
      mode: 'single';
      folderId: string;
      folderName: string;
      controller: AbortController;
      progress: FolderDownloadProgress | null;
      failure: DownloadFailure | null;
    }
  | {
      mode: 'confirm-parts';
      folderId: string;
      folderName: string;
      fileCount: number;
      partCount: number;
    }
  | {
      mode: 'parts';
      folderId: string;
      folderName: string;
      fileCount: number;
      controller: AbortController;
      partsProgress: FolderPartsProgress | null;
      failure: DownloadFailure | null;
      /** The part that failed — where "Continue" picks up. */
      resume: FolderPartLocation | null;
    };

/** What a failed download shows the operator, and whether anything can be done. */
interface DownloadFailure {
  message: string;
  /** Retrying can plausibly succeed. */
  canRetry: boolean;
}

function DownloadIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

function MoveIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M5 9V5a2 2 0 0 1 2-2h4l2 2h6a2 2 0 0 1 2 2v3" />
      <line x1="3" y1="14" x2="15" y2="14" />
      <polyline points="11 10 15 14 11 18" />
    </svg>
  );
}

function toDownloadView(state: FolderDownloadState): FolderDownloadView {
  switch (state.mode) {
    case 'single':
      return {
        kind: 'progress',
        progress: state.progress,
        error: state.failure?.message ?? null,
        canRetry: state.failure?.canRetry ?? false,
      };
    case 'confirm-parts':
      return {
        kind: 'confirm-parts',
        fileCount: state.fileCount,
        partCount: state.partCount,
        pageSize: DOWNLOAD_PAGE_SIZE,
      };
    case 'parts':
      return {
        kind: 'parts',
        partsProgress: state.partsProgress,
        error: state.failure?.message ?? null,
        canRetry: state.failure?.canRetry ?? false,
        resume: state.resume,
      };
  }
}

/**
 * A cancellation, from either this run's controller or the zip's internal one.
 * Never an incident: no alert, no error modal.
 */
function isAbortError(err: unknown): boolean {
  const cause = err instanceof FolderPartsError ? err.cause : err;
  return cause instanceof DOMException && cause.name === 'AbortError';
}

/** A download whose modal is showing an error (and may be retried). */
function isFailedDownload(state: FolderDownloadState): boolean {
  return state.mode !== 'confirm-parts' && state.failure !== null;
}

/**
 * What a failure means for the operator and for Sentry. `report` is consumed at
 * the call site; only `message`/`canRetry` are worth keeping in state.
 *
 * `report` is false for outcomes that are expected states rather than incidents:
 * an empty folder, and a file that no longer exists (S3 404 — deleted mid-run,
 * or a stale key). Everything else is the class this reporting was added for —
 * transient S3 trouble, or something unforeseen. Aborts are filtered by callers.
 *
 * `canRetry` is a different question, and a 404 is the case where the two answers
 * differ: retrying re-fetches the manifest from scratch, so the deleted file is
 * simply no longer in the list and the download succeeds. That is the one failure
 * a retry reliably cures, so it must not be dead-ended. Only an empty folder has
 * nothing a retry could change.
 */
function classifyDownloadFailure(err: unknown): DownloadFailure & { report: boolean } {
  const cause = err instanceof FolderPartsError ? err.cause : err;
  const expected =
    cause instanceof FolderEmptyError || (cause instanceof FileFetchError && cause.status === 404);
  return {
    message: describeDownloadFailure(cause),
    canRetry: !(cause instanceof FolderEmptyError),
    report: !expected,
  };
}

/**
 * Operator-facing text. `FileFetchError`'s own message is fixed so Sentry groups
 * it as one issue — the tenant's file path and the varying reason are added back
 * here, for the modal. (The path is not put in the error message or the event
 * payload; an unmasked session replay can still capture the rendered modal — see
 * the note on `FileFetchError`.)
 */
function describeDownloadFailure(cause: unknown): string {
  if (cause instanceof FileFetchError) {
    if (cause.status === 404) {
      // Retrying rebuilds the file list, so the missing file drops out of it.
      return `"${cause.relativePath}" was deleted or moved during the download. Retrying will skip it.`;
    }
    const what =
      cause.status !== null
        ? `the storage service returned ${cause.status}`
        : 'it could not be downloaded after several attempts';
    return `Could not download "${cause.relativePath}" — ${what}.`;
  }
  return getErrorMessage(cause);
}

/**
 * Folder downloads catch their own errors to show them in the modal, which hides
 * them from Sentry's global handlers — report explicitly, so a failure hours
 * into a long download is visible and says which part it died on.
 */
function reportFolderDownloadFailure(err: unknown, context: Record<string, unknown>) {
  const cause = err instanceof FolderPartsError ? err.cause : err;
  const at = err instanceof FolderPartsError ? err.at : undefined;
  Sentry.captureException(err, {
    tags: { feature: 'admin-folder-download' },
    extra: {
      ...context,
      part: at?.part,
      totalParts: at?.totalParts,
      offset: at?.offset,
      // The path is tenant data and is deliberately kept out of this payload;
      // the id and the reason identify the failure without it.
      fileId: cause instanceof FileFetchError ? cause.fileId : undefined,
      detail: cause instanceof FileFetchError ? cause.detail : undefined,
      status: cause instanceof FileFetchError ? cause.status : undefined,
    },
  });
}

export function UploadsTab({ orgId, projectId, currentFolderId, onFolderChange, rootFolderId, projectRootFolderId, projectName, onBackToProjects, adminRole, availableProjects }: UploadsTabProps) {
  // When at the top level of a project, use the project's root folder for API calls
  const effectiveFolderId = currentFolderId ?? projectRootFolderId ?? null;
  const canDownload = adminRole !== 'admin_viewer';
  // Editors/super_admins can move a folder to another project (same gate as other mutations).
  const canEdit = adminRole !== 'admin_viewer';

  // Filename search (component-local; intentionally not persisted to the URL hash in v1).
  const [searchRaw, setSearchRaw] = useState('');
  const [scope, setScope] = useState<UploadSearchScope>('folder');
  const debouncedSearch = useDebouncedValue(searchRaw, 300);
  // Collapse to '' the instant the box is empty (don't wait for the debounce) so clearing
  // / navigating takes effect in the same render. Otherwise require >= 2 chars (a 1-char
  // term matches almost everything; 3+ chars use the GIN trigram index idx_files_original_name_trgm).
  const effectiveQ =
    searchRaw.trim() === '' ? '' : debouncedSearch.trim().length >= 2 ? debouncedSearch.trim() : '';
  const isSearching = effectiveQ !== '';
  // Project-wide scope needs a project to search within (admin lists by org+folder) and only
  // takes effect while actively searching; an empty box is always folder scope.
  const isProjectScope = isSearching && scope === 'project' && Boolean(projectId);
  const canSearchProject = Boolean(projectId);

  const [currentPath, setCurrentPath] = useState<Folder[]>([]);
  const [folders, setFolders] = useState<FolderWithMeta[]>([]);
  const [selectedFile, setSelectedFile] = useState<FileUpload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rootFolderDescription, setRootFolderDescription] = useState<string | null>(null);

  // File pagination. `files` is the cumulative loaded set; "Load more" appends
  // (deduped by id) and an in-flight page is discarded if the folder changes.
  const fetchFilesPage = useCallback(
    async (page: number) => {
      const result = await api.listOrgUploads(
        orgId,
        // Project scope searches every folder, so don't constrain by the current one.
        isProjectScope ? null : effectiveFolderId,
        { page, q: effectiveQ || undefined, scope: isProjectScope ? 'project' : 'folder', projectId },
      );
      return { items: result.uploads, pagination: result.pagination };
    },
    [orgId, effectiveFolderId, effectiveQ, isProjectScope, projectId],
  );
  // Folding scope + query into scopeKey makes a new search reset to page 1 (via the
  // loadContents effect) and invalidates any in-flight Load More. The same key guards the
  // component-owned page-1 fetch (see scopeKeyRef).
  const scopeKey = `${isProjectScope ? 'project' : (effectiveFolderId ?? 'root')}|${effectiveQ}`;
  const filePagination = usePaginatedList<FileUpload>({
    scopeKey,
    fetchPage: fetchFilesPage,
  });
  const files = filePagination.items;

  // Reflects the view currently shown (folder + search term + scope), so a slow page-1/path
  // response can't apply to a view the admin has since changed.
  const scopeKeyRef = useRef(scopeKey);
  scopeKeyRef.current = scopeKey;
  // Guards loadPath (breadcrumb/description), which is navigation-scoped, not search-scoped.
  const currentFolderIdRef = useRef(currentFolderId);
  currentFolderIdRef.current = currentFolderId;

  const [fileDownloading, setFileDownloading] = useState<string | null>(null);
  const [folderDownload, setFolderDownload] = useState<FolderDownloadState | null>(null);
  const folderDownloadRef = useRef<FolderDownloadState | null>(null);
  folderDownloadRef.current = folderDownload;

  /**
   * Set the download state AND the ref that guards it, in one step. The ref is
   * otherwise only refreshed on render, so two clicks in the same tick (a fast
   * double-click on Retry) would both pass the "one at a time" guard and start
   * concurrent runs.
   */
  const setFolderDownloadNow = (next: FolderDownloadState | null) => {
    folderDownloadRef.current = next;
    setFolderDownload(next);
  };

  // Leaving the tab unmounts this component: stop the run rather than let it
  // continue with zips still landing in Downloads and no modal to cancel it.
  useEffect(
    () => () => {
      const current = folderDownloadRef.current;
      if (current && current.mode !== 'confirm-parts' && !current.failure) {
        current.controller.abort();
      }
    },
    []
  );

  // Cross-project move: the folder whose move modal is open (null = closed).
  const [movingFolder, setMovingFolder] = useState<FolderWithMeta | null>(null);

  const { setFirstPage } = filePagination;
  const loadContents = useCallback(async () => {
    const requestScope = scopeKey;
    try {
      setLoading(true);
      setError('');

      const [foldersResult, uploadsResult] = await Promise.all([
        // Search matches folders too: project scope searches every folder
        // (parentId omitted), folder scope lists/searches the current folder.
        api.listOrgFolders(
          orgId,
          isProjectScope ? null : effectiveFolderId,
          { q: effectiveQ || undefined, scope: isProjectScope ? 'project' : 'folder', projectId },
        ),
        fetchFilesPage(1),
      ]);

      // Bail if the view (folder / search term / scope) changed while in flight.
      if (scopeKeyRef.current !== requestScope) return;

      setFolders(foldersResult.folders.filter(f => f.name !== '__root__'));
      setFirstPage(uploadsResult);
      // Project-wide folder search is capped server-side; tell the admin if it hit the cap.
      if (foldersResult.truncated) {
        setError(
          `Showing the first ${foldersResult.folders.length.toLocaleString()} matching folders; refine your search to see more.`,
        );
      }
    } catch (err) {
      if (scopeKeyRef.current !== requestScope) return;
      setError(getErrorMessage(err));
    } finally {
      if (scopeKeyRef.current === requestScope) setLoading(false);
    }
  }, [scopeKey, orgId, effectiveFolderId, fetchFilesPage, setFirstPage]);

  // Breadcrumb path + folder description are navigation-scoped — kept separate from the
  // search-driven loadContents so they aren't refetched on every debounced search change.
  const loadPath = useCallback(async () => {
    const requested = currentFolderId;
    if (currentFolderId) {
      try {
        const folderResult = await api.getOrgFolder(orgId, currentFolderId);
        if (currentFolderIdRef.current !== requested) return;
        setCurrentPath(folderResult.path.filter(f => f.name !== '__root__'));
        setRootFolderDescription(null);
      } catch {
        if (currentFolderIdRef.current !== requested) return;
        setCurrentPath([]);
        setRootFolderDescription(null);
      }
      return;
    }
    setCurrentPath([]);
    const descFolderId = projectRootFolderId ?? rootFolderId;
    if (!descFolderId) {
      setRootFolderDescription(null);
      return;
    }
    try {
      const { folder } = await api.getOrgFolder(orgId, descFolderId);
      if (currentFolderIdRef.current !== requested) return;
      setRootFolderDescription(folder.description);
    } catch {
      if (currentFolderIdRef.current !== requested) return;
      setRootFolderDescription(null);
    }
  }, [orgId, currentFolderId, projectRootFolderId, rootFolderId]);

  useEffect(() => {
    loadContents();
  }, [loadContents]);

  useEffect(() => {
    loadPath();
  }, [loadPath]);

  // Entering a folder clears any active search and reverts to folder scope, so
  // the destination shows that folder's contents rather than a stale result set.
  const navigateToFolder = (folderId: string | null) => {
    setSearchRaw('');
    setScope('folder');
    onFolderChange(folderId);
    setSelectedFile(null);
  };

  // From a project-wide search result, jump to the folder a match lives in. A
  // root-level match (folder_id null, or the project's __root__ folder) goes to root.
  const navigateToResultFolder = (folderId: string | null) => {
    navigateToFolder(folderId && folderId !== projectRootFolderId ? folderId : null);
  };

  const handleFileDownload = async (file: FileUpload) => {
    if (!canDownload || fileDownloading) return;
    setFileDownloading(file.id);
    setError('');
    try {
      const { url, filename } = await api.getFileDownloadUrl(orgId, file.id);
      triggerBrowserDownload(url, filename);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setFileDownloading(null);
    }
  };

  const startFolderDownload = async (folderId: string, folderName: string) => {
    // One download at a time — except "Retry" from a modal showing an error.
    const current = folderDownloadRef.current;
    if (!canDownload || (current && !isFailedDownload(current))) return;

    const controller = new AbortController();
    // Guard every state update on this run's own controller: a late rejection
    // from a cancelled run must not wipe a download started afterwards.
    const isMine = (state: FolderDownloadState | null): state is Extract<FolderDownloadState, { mode: 'single' }> =>
      state?.mode === 'single' && state.controller === controller;
    setFolderDownloadNow({
      mode: 'single',
      folderId,
      folderName,
      controller,
      progress: null,
      failure: null,
    });

    // A folder download can run for hours. `busyTracker` defers the version
    // watcher's auto-refresh and the expired-session redirect (shared reloadGate)
    // until it finishes, so a push to main can't reload the tab mid-download.
    // Released in `finally`, which is what reloadGate relies on.
    busyTracker.begin();
    try {
      await downloadFolderAsZip(orgId, folderId, {
        signal: controller.signal,
        onProgress: (progress) => {
          setFolderDownload(prev => (isMine(prev) ? { ...prev, progress } : prev));
        },
      });
      setFolderDownload(prev => (isMine(prev) ? null : prev));
    } catch (err) {
      if (controller.signal.aborted || isAbortError(err)) {
        setFolderDownload(prev => (isMine(prev) ? null : prev));
        return;
      }
      // Too large for one zip — offer a chunked, multi-part download as an
      // explicitly-confirmed second step.
      if (err instanceof FolderTooLargeError) {
        const fileCount = err.total;
        const partCount = Math.ceil(fileCount / DOWNLOAD_PAGE_SIZE);
        setFolderDownload(prev =>
          isMine(prev) ? { mode: 'confirm-parts', folderId, folderName, fileCount, partCount } : prev
        );
        return;
      }
      const { report, ...failure } = classifyDownloadFailure(err);
      if (report) reportFolderDownloadFailure(err, { orgId, folderId, mode: 'single' });
      setFolderDownload(prev => (isMine(prev) ? { ...prev, failure } : prev));
    } finally {
      busyTracker.end();
    }
  };

  const confirmPartsDownload = () => {
    const current = folderDownloadRef.current;
    if (!current || current.mode !== 'confirm-parts') return;
    void runPartsDownload(current.folderId, current.folderName, current.fileCount);
  };

  /**
   * Run the multi-part download, optionally continuing from the part that
   * failed. The zips already saved are not fetched again — for the folder that
   * motivated this change (19 parts, ~2 h, died on part 19) a retry is one part,
   * not the whole folder.
   */
  const runPartsDownload = async (
    folderId: string,
    folderName: string,
    fileCount: number,
    resumeFrom?: FolderPartLocation
  ) => {
    const controller = new AbortController();
    const isMine = (state: FolderDownloadState | null): state is Extract<FolderDownloadState, { mode: 'parts' }> =>
      state?.mode === 'parts' && state.controller === controller;
    setFolderDownloadNow({
      mode: 'parts',
      folderId,
      folderName,
      fileCount,
      controller,
      // Seeded so a continued run names its part during the manifest round-trip
      // instead of showing "part 0 of 0".
      partsProgress: resumeFrom
        ? {
            totalParts: resumeFrom.totalParts,
            completedParts: resumeFrom.part - 1,
            currentPart: resumeFrom.part,
            fileProgress: null,
          }
        : null,
      failure: null,
      resume: resumeFrom ?? null,
    });

    busyTracker.begin();
    try {
      await downloadFolderInParts(orgId, folderId, folderName, fileCount, {
        signal: controller.signal,
        resumeFrom,
        onProgress: (partsProgress) => {
          setFolderDownload(prev => (isMine(prev) ? { ...prev, partsProgress } : prev));
        },
      });
      // Leave the modal open on the completion summary; the user closes it.
    } catch (err) {
      if (controller.signal.aborted || isAbortError(err)) {
        setFolderDownload(prev => (isMine(prev) ? null : prev));
        return;
      }
      const { report, ...failure } = classifyDownloadFailure(err);
      if (report) {
        reportFolderDownloadFailure(err, { orgId, folderId, mode: 'parts', fileCount, continued: !!resumeFrom });
      }
      // Every failure this raises carries the part it died on, so the operator
      // can continue rather than redo the parts already on disk.
      const resume = err instanceof FolderPartsError ? err.at : null;
      setFolderDownload(prev => (isMine(prev) ? { ...prev, failure, resume } : prev));
    } finally {
      busyTracker.end();
    }
  };

  /** Retry a single zip from scratch, or continue a parts run from the failed part. */
  const retryFolderDownload = () => {
    const current = folderDownloadRef.current;
    if (!current || current.mode === 'confirm-parts' || !current.failure?.canRetry) return;
    if (current.mode === 'single') {
      void startFolderDownload(current.folderId, current.folderName);
      return;
    }
    if (current.resume) {
      void runPartsDownload(current.folderId, current.folderName, current.fileCount, current.resume);
    }
  };

  const cancelFolderDownload = () => {
    const current = folderDownloadRef.current;
    if (current && current.mode !== 'confirm-parts') {
      current.controller.abort();
    }
    setFolderDownloadNow(null);
  };

  const closeFolderDownloadModal = () => {
    setFolderDownloadNow(null);
  };

  // Fired as soon as the move commits: refresh the listing (the moved folder
  // disappears from this project) and drop any selection. The modal stays open
  // to show the file-relocation progress; closing it is a separate action.
  const handleFolderMoved = () => {
    setSelectedFile(null);
    loadContents();
  };

  // Folders are searched alongside files, so they show during search too.
  const showFolders = folders.length > 0;
  const isEmpty = folders.length === 0 && files.length === 0;
  const currentFolder = currentPath.length > 0 ? currentPath[currentPath.length - 1] : null;

  return (
    <div className="uploads-tab">
      {/* Breadcrumb */}
      <div className="uploads-toolbar">
        <div className="uploads-breadcrumb">
          {onBackToProjects && (
            <>
              <button className="breadcrumb-item breadcrumb-back" onClick={onBackToProjects}>
                ← Projects
              </button>
              <span className="breadcrumb-separator">/</span>
            </>
          )}
          <button
            className={`breadcrumb-item ${currentFolderId === null ? 'active' : ''}`}
            onClick={() => navigateToFolder(null)}
          >
            {projectName ?? 'Home'}
          </button>
          {currentPath.map((folder, index) => (
            <span key={folder.id}>
              <span className="breadcrumb-separator">/</span>
              <button
                className={`breadcrumb-item ${index === currentPath.length - 1 ? 'active' : ''}`}
                onClick={() => navigateToFolder(folder.id)}
              >
                {folder.name}
              </button>
            </span>
          ))}
        </div>
        {canDownload && currentFolder && (
          <button
            type="button"
            className="btn-secondary btn-download-folder"
            onClick={() => startFolderDownload(currentFolder.id, currentFolder.name)}
            disabled={!!folderDownload}
            title="Download this folder as a zip"
          >
            <DownloadIcon />
            Download folder
          </button>
        )}
        {canDownload && !currentFolder && projectRootFolderId && !isEmpty && (
          <button
            type="button"
            className="btn-secondary btn-download-folder"
            onClick={() => startFolderDownload(projectRootFolderId, projectName ?? 'Home')}
            disabled={!!folderDownload}
            title="Download all files as a zip"
          >
            <DownloadIcon />
            Download all
          </button>
        )}
      </div>

      {/* Filename search */}
      <div className="uploads-search">
        <div className="search-input-wrap">
          <SearchIcon />
          <input
            type="search"
            className="search-input"
            placeholder="Search files and folders…"
            value={searchRaw}
            onChange={(e) => setSearchRaw(e.target.value)}
            aria-label="Search files and folders by name"
          />
          {searchRaw && (
            <button
              type="button"
              className="search-clear"
              onClick={() => setSearchRaw('')}
              title="Clear search"
              aria-label="Clear search"
            >
              ×
            </button>
          )}
        </div>
        {canSearchProject && (
          <label className="search-scope-toggle">
            <input
              type="checkbox"
              checked={scope === 'project'}
              onChange={(e) => setScope(e.target.checked ? 'project' : 'folder')}
            />
            <span>Search entire project</span>
          </label>
        )}
        {searchRaw.trim().length === 1 && (
          <span className="search-hint">Type at least 2 characters…</span>
        )}
      </div>

      {/* Folder Description */}
      {currentPath.length > 0 && currentPath[currentPath.length - 1].description && (
        <div className="folder-description">
          {currentPath[currentPath.length - 1].description}
        </div>
      )}
      {currentPath.length === 0 && rootFolderDescription && (
        <div className="folder-description">
          {rootFolderDescription}
        </div>
      )}

      {error && <div className="error-message">{error}</div>}

      {loading ? (
        <div className="loading">Loading...</div>
      ) : (
        <div className="uploads-content">
          <div className="uploads-main">
            {isEmpty ? (
              <div className="empty-state">
                {isSearching
                  ? `No files or folders match “${effectiveQ}”${isProjectScope ? ' in this project' : ' in this folder'}`
                  : currentFolderId
                    ? 'This folder is empty'
                    : 'No files uploaded yet'}
              </div>
            ) : (
              <>
                {/* Folders (shown during search too — they're searched alongside files) */}
                {showFolders && (
                  <div className="uploads-section">
                    <h4>Folders</h4>
                    <div className="folders-grid">
                      {folders.map((folder) => (
                        <div
                          key={folder.id}
                          className="folder-item"
                          onClick={() => navigateToFolder(folder.id)}
                        >
                          {canEdit && projectId && (
                            <button
                              type="button"
                              className="folder-move-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                setMovingFolder(folder);
                              }}
                              aria-label={`Move folder ${folder.name} to another project`}
                              title="Move to another project"
                            >
                              <MoveIcon />
                            </button>
                          )}
                          {canDownload && (
                            <button
                              type="button"
                              className="folder-download-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                startFolderDownload(folder.id, folder.name);
                              }}
                              disabled={!!folderDownload}
                              aria-label={`Download folder ${folder.name}`}
                              title="Download folder"
                            >
                              <DownloadIcon />
                            </button>
                          )}
                          <div className="folder-icon">
                            <FolderIcon />
                          </div>
                          <span className="folder-name">{highlightMatch(folder.name, effectiveQ)}</span>
                          {folder.folder_path !== undefined && (
                            <button
                              type="button"
                              className="folder-folder-path"
                              title={`Go to ${folder.folder_path || 'project root'}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                navigateToResultFolder(folder.parent_id);
                              }}
                            >
                              <FolderPathIcon />
                              <span className="folder-folder-path-text">{folder.folder_path || 'Project root'}</span>
                            </button>
                          )}
                          <span className="folder-meta">
                            {folder.subfolder_count > 0 && `${folder.subfolder_count} folders`}
                            {folder.subfolder_count > 0 && folder.file_count > 0 && ', '}
                            {folder.file_count > 0 && `${folder.file_count} files`}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Files */}
                {files.length > 0 && (
                  <div className="uploads-section">
                    <h4>
                      Files
                      {filePagination.total > files.length && (
                        <span className="uploads-count">
                          {' '}(showing {files.length.toLocaleString()} of {filePagination.total.toLocaleString()})
                        </span>
                      )}
                    </h4>
                    <div className="files-list">
                      {files.map((file) => (
                        <div
                          key={file.id}
                          className={`file-item ${selectedFile?.id === file.id ? 'selected' : ''}`}
                          onClick={() => setSelectedFile(file)}
                        >
                          <span className="file-icon">
                            <FileIcon type={getFileType(file.content_type, file.file_name)} />
                          </span>
                          <div className="file-name-cell">
                            <span className="file-name">{highlightMatch(file.file_name, effectiveQ)}</span>
                            {/* Renamed file matched on its original name — show why it's a result. */}
                            {effectiveQ &&
                              file.file_name.toLowerCase().indexOf(effectiveQ.toLowerCase()) === -1 &&
                              file.original_name.toLowerCase().indexOf(effectiveQ.toLowerCase()) !== -1 && (
                                <span className="file-original-name" title={`Originally: ${file.original_name}`}>
                                  originally {highlightMatch(file.original_name, effectiveQ)}
                                </span>
                              )}
                            {/* Project-wide search: show (and link to) where the match lives. */}
                            {file.folder_path !== undefined && (
                              <button
                                type="button"
                                className="file-folder-path"
                                title={`Go to ${file.folder_path || 'project root'}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigateToResultFolder(file.folder_id);
                                }}
                              >
                                <FolderPathIcon />
                                <span className="file-folder-path-text">{file.folder_path || 'Project root'}</span>
                              </button>
                            )}
                          </div>
                          <span className="file-size">{formatFileSize(file.file_size)}</span>
                          <span className="file-date">
                            {file.completed_at
                              ? new Date(file.completed_at).toLocaleDateString()
                              : ''}
                          </span>
                        </div>
                      ))}
                    </div>
                    {filePagination.hasMore && (
                      <div className="uploads-load-more">
                        {filePagination.loadMoreError && (
                          <p className="uploads-load-more-error" role="alert">{filePagination.loadMoreError}</p>
                        )}
                        <button
                          type="button"
                          className="btn-secondary"
                          onClick={filePagination.loadMore}
                          disabled={filePagination.isLoadingMore}
                          aria-busy={filePagination.isLoadingMore}
                        >
                          {filePagination.isLoadingMore
                            ? 'Loading…'
                            : `${filePagination.loadMoreError ? 'Retry — load more' : 'Load more'} (${files.length.toLocaleString()} of ${filePagination.total.toLocaleString()})`}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {/* File Details Panel */}
          {selectedFile && (
            <div className="file-details-panel">
              <div className="panel-header">
                <h4>File Details</h4>
                <button className="panel-close" onClick={() => setSelectedFile(null)}>×</button>
              </div>
              <div className="panel-content">
                {canDownload && (
                  <button
                    type="button"
                    className="btn-primary btn-download-file"
                    onClick={() => handleFileDownload(selectedFile)}
                    disabled={fileDownloading === selectedFile.id}
                  >
                    <DownloadIcon />
                    {fileDownloading === selectedFile.id ? 'Preparing…' : 'Download'}
                  </button>
                )}
                <div className="detail-row">
                  <label>Name</label>
                  <span>{selectedFile.file_name}</span>
                </div>
                {selectedFile.original_name !== selectedFile.file_name && (
                  <div className="detail-row">
                    <label>Original Name</label>
                    <span>{selectedFile.original_name}</span>
                  </div>
                )}
                <div className="detail-row">
                  <label>Size</label>
                  <span>{formatFileSize(selectedFile.file_size)}</span>
                </div>
                <div className="detail-row">
                  <label>Type</label>
                  <span>{selectedFile.content_type}</span>
                </div>
                <div className="detail-row">
                  <label>Uploaded By</label>
                  <span>
                    {selectedFile.uploaded_by_first_name} {selectedFile.uploaded_by_last_name}
                    <br />
                    <small>{selectedFile.uploaded_by_email}</small>
                  </span>
                </div>
                <div className="detail-row">
                  <label>Upload Date</label>
                  <span>
                    {selectedFile.completed_at
                      ? new Date(selectedFile.completed_at).toLocaleString()
                      : 'Pending'}
                  </span>
                </div>
                {selectedFile.description && (
                  <div className="detail-row">
                    <label>Description</label>
                    <div className="file-description">{selectedFile.description}</div>
                  </div>
                )}
                <div className="detail-row">
                  <label>S3 Key</label>
                  <code className="s3-key">{selectedFile.s3_key}</code>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {folderDownload && (
        <FolderDownloadModal
          folderName={folderDownload.folderName}
          view={toDownloadView(folderDownload)}
          onCancel={cancelFolderDownload}
          onClose={closeFolderDownloadModal}
          onConfirmParts={confirmPartsDownload}
          onRetry={retryFolderDownload}
        />
      )}

      {movingFolder && projectId && (
        <MoveFolderModal
          orgId={orgId}
          folder={movingFolder}
          currentProjectId={projectId}
          availableProjects={availableProjects ?? []}
          onClose={(incomplete) => {
            setMovingFolder(null);
            // Durable notice if the operator closed before relocation finished:
            // the move committed, but some files are still on the old storage key.
            if (incomplete) {
              const left = incomplete.total - incomplete.relocated;
              setError(
                `Folder moved, but ${left.toLocaleString()} of ${incomplete.total.toLocaleString()} ` +
                  `file(s) aren't relocated in storage yet — they'll finish on a later move.`,
              );
            }
          }}
          onMoved={handleFolderMoved}
        />
      )}
    </div>
  );
}
